/**
 * Utilidades de Sello de Conformidad Digital para templates de impresión.
 * Reutilizable para cualquier tipo de acta (inspección, atención, diaria, futuras).
 */

/**
 * Genera el HTML del sello de conformidad digital del testigo.
 * @param {Object} sealData - Datos del sello del endpoint /api/seal/
 * @returns {string} HTML string del sello
 */
export function renderSealHtml(sealData) {
  if (!sealData?.aprobado) {
    return '';
  }

  return `
    <div style="border:2px solid #2e7d32; border-radius:8px; padding:12px; background:#f1f8e9; max-width:280px; margin:0 auto;">
      <div style="text-align:center; margin-bottom:8px;">
        <span style="color:#2e7d32; font-size:14px; font-weight:700;">&#10003; CONFORMIDAD DIGITAL VERIFICADA</span>
      </div>
      <div style="font-size:10px; color:#333; line-height:1.6;">
        <div><strong>Por:</strong> ${sealData.testigo_nombre || ''}</div>
        <div><strong>Contacto:</strong> ${sealData.testigo_contacto || ''}</div>
        <div><strong>Canal:</strong> ${sealData.canal || ''}</div>
        <div><strong>Fecha:</strong> ${sealData.timestamp_display || ''}</div>
        <div><strong>IP:</strong> ${sealData.ip || ''}</div>
        <div><strong>Dispositivo:</strong> ${sealData.dispositivo || ''} - ${sealData.browser || ''} - ${sealData.os || ''}</div>
      </div>
      <div style="display:flex; align-items:center; margin-top:8px; padding-top:8px; border-top:1px dashed #a5d6a7;">
        <img src="${sealData.qr_base64 || ''}" alt="QR Verificación" style="width:70px; height:70px; margin-right:8px;" />
        <div style="font-size:9px; color:#555; flex:1;">
          <div style="margin-bottom:3px; word-break:break-all;"><strong>Código:</strong> ${sealData.hash || ''}</div>
          <div>Escanee el QR para verificar la autenticidad de este documento</div>
        </div>
      </div>
      <div style="font-size:8px; color:#888; text-align:center; margin-top:6px;">
        Ref. Auditoría: ${sealData.audit_ref || ''}
      </div>
    </div>
  `;
}

/**
 * Genera el HTML del bloque de firma del supervisor.
 * - Con rubrica: imagen + nombre + documento
 * - Sin rubrica: solo nombre + documento (sin espacio vacío)
 * @param {Object} requerimiento - Datos del requerimiento con supervisor_*
 * @returns {string} HTML string
 */
export function renderSupervisorSignatureHtml(requerimiento) {
  const nombre = requerimiento?.supervisor_nombre || '';
  const rubricaUrl = requerimiento?.supervisor_rubrica_url;
  const tipoDoc = requerimiento?.supervisor_tipo_documento || '';
  const docId = requerimiento?.supervisor_documento_identidad || '';
  const docHtml = (tipoDoc && docId) ? `${tipoDoc}: ${docId}` : '';

  if (rubricaUrl) {
    return `
      <div style="text-align:center;">
        <div style="height:60px; display:flex; align-items:center; justify-content:center; margin-bottom:5px;">
          <img src="${rubricaUrl}" alt="Rúbrica" style="max-height:60px; max-width:150px; object-fit:contain;" />
        </div>
        <div style="border-bottom:1px solid #333; margin-bottom:8px;"></div>
        <p style="font-size:10px; font-weight:bold; text-align:center; margin-bottom:5px;">Firma del Representante de la Empresa</p>
        <p style="font-size:10px; text-align:center; margin-bottom:3px;">${nombre}</p>
        ${docHtml ? `<p style="font-size:10px; text-align:center;">${docHtml}</p>` : ''}
      </div>
    `;
  }

  return `
    <div style="text-align:center; padding:10px 0;">
      <div style="border-bottom:1px solid #333; width:80%; margin:0 auto 8px auto;"></div>
      <p style="font-size:10px; font-weight:bold; text-align:center; margin-bottom:5px;">Firma del Representante de la Empresa</p>
      <p style="font-size:10px; text-align:center; margin-bottom:3px;">${nombre}</p>
      ${docHtml ? `<p style="font-size:10px; text-align:center;">${docHtml}</p>` : ''}
    </div>
  `;
}

/**
 * Genera el HTML del bloque completo del testigo.
 * - Con sello: nombre + doc + sello digital
 * - Sin sello: solo nombre + doc
 * @param {Object} testigo - {nombre, dni}
 * @param {Object|null} sealData - Datos del sello o null
 * @returns {string} HTML string
 */
export function renderTestigoInfoHtml(testigo, sealData, isDigitalProcess = false, actaAprobada = false) {
  const nombre = testigo?.nombre || '';
  const dni = testigo?.dni || '';

  if (sealData?.aprobado) {
    return `
      <div style="text-align:center;">
        <p style="font-size:10px; font-weight:bold; text-align:center; margin-bottom:5px;">Cliente / Testigo</p>
        <p style="font-size:10px; text-align:center; margin-bottom:3px;">${nombre}</p>
        ${dni ? `<p style="font-size:10px; text-align:center; margin-bottom:8px;">Doc. Identidad: ${dni}</p>` : ''}
        ${renderSealHtml(sealData)}
      </div>
    `;
  }

  if (actaAprobada) {
    return `
      <div style="text-align:center; padding:10px 0;">
        <p style="font-size:12px; font-weight:bold; color:#2e7d32; text-align:center; margin-bottom:8px;">APROBADO DIGITALMENTE</p>
        <div style="border-bottom:1px solid #333; width:80%; margin:0 auto 8px auto;"></div>
        <p style="font-size:10px; font-weight:bold; text-align:center; margin-bottom:5px;">Firma del Propietario / Testigo</p>
        ${nombre ? `<p style="font-size:10px; text-align:center; margin-bottom:3px;">${nombre}</p>` : ''}
        ${dni ? `<p style="font-size:10px; text-align:center;">Doc. Identidad: ${dni}</p>` : ''}
      </div>
    `;
  }

  if (isDigitalProcess) {
    return `
      <div style="text-align:center; padding:10px 0;">
        <p style="font-size:10px; font-weight:bold; text-align:center; margin-bottom:5px;">Conformidad digital del Propietario / Testigo</p>
        ${nombre ? `<p style="font-size:10px; text-align:center; margin-bottom:3px;">${nombre}</p>` : ''}
        ${dni ? `<p style="font-size:10px; text-align:center;">Doc. Identidad: ${dni}</p>` : ''}
      </div>
    `;
  }

  return `
    <div style="text-align:center; padding:10px 0;">
      <div style="border-bottom:1px solid #333; width:80%; margin:0 auto 8px auto;"></div>
      <p style="font-size:10px; font-weight:bold; text-align:center; margin-bottom:5px;">Firma del Propietario / Testigo</p>
      ${nombre ? `<p style="font-size:10px; text-align:center; margin-bottom:3px;">${nombre}</p>` : ''}
      ${dni ? `<p style="font-size:10px; text-align:center;">Doc. Identidad: ${dni}</p>` : ''}
    </div>
  `;
}
